import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'react-toastify';
import SideBar from './SideBar';
import authApi from '../auth.api';
import { IG_VIDEOS, UPLOAD_TO_INSTAGRAM } from '../route';
import VideoShowcaseButton from './series/my-series/video-showcase/VideoShowcaseButton';
import {
  Pause,
  Play,
  Volume2,
  VolumeX,
  X,
  Youtube,
  Video as TikTokIcon,
  Upload,
} from 'lucide-react';

const VideoShowcaseStatusIndicator = ({ isPublished, platform }) => {
  const Icon = platform === 'YouTube' ? Youtube : TikTokIcon;
  return (
    <div
      className={`flex items-center space-x-1 ${
        isPublished ? 'text-green-500' : 'text-gray-400'
      }`}
    >
      <Icon size={16} />
      <span className='text-xs font-medium'>
        {isPublished ? 'Published' : 'Not Published'}
      </span>
    </div>
  );
};

const ForIgVideos = () => {
  const [videos, setVideos] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    fetchAllVideos();
  }, []);
  const fetchAllVideos = async () => {
    try {
      const { data } = await authApi.get(IG_VIDEOS);
      setVideos(data.videos);
    } catch (error) {
      toast.error('Failed to load series. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const [playingVideoId, setPlayingVideoId] = useState(null);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [isFullscreenPlaying, setIsFullscreenPlaying] = useState(false);
  const [isFullscreenMuted, setIsFullscreenMuted] = useState(true);

  const handleClose = () => {
    setSelectedVideo(null);
    setIsFullscreenPlaying(false);
  };
  const onPublish = (params, source) => {
    handleVideoAction(params._id, `upload to ${source}`);
  };
  const handleVideoAction = async (id, action, videoName) => {
    toast.success('Queued for upload');
    const result = await authApi.post(`${UPLOAD_TO_INSTAGRAM}/${id}`);
    if (result.status) {
      toast.success('Instagram upload successful!');
      return fetchAllVideos();
    }
    toast.error('unable to upload')
  };
  const toggleFullscreenPlay = () => {
    setIsFullscreenPlaying(!isFullscreenPlaying);
  };
  const toggleFullscreenMute = () => {
    setIsFullscreenMuted(!isFullscreenMuted);
  };

  const [showOverlay, setShowOverlay] = useState(false);
  const videoRef = useRef(null);
  const timeoutRef = useRef(null);
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const handleMouseEnter = () => {
    setShowOverlay(true);
  };
  const handleMouseLeave = () => {
    setShowOverlay(false);
  };

  return (
    <div className='flex h-screen bg-gray-50'>
      <SideBar />
      <div className='flex-1 overflow-auto px-6 py-8 md:p-8'>
        <div className='max-w-7xl mx-auto space-y-8'>
          {/* Header */}
          <div className='flex items-center justify-between'>
            <h2 className='text-2xl md:text-3xl lg:text-4xl font-bold text-gray-800 tracking-tight'>
              Videos
            </h2>
          </div>

          {/* Main Content */}

          <div className='space-y-8'>
            <div className='w-full'>
              <div className='grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-4 gap-3'>
                {videos.map((video) => (
                  <div
                    className='relative aspect-[9/16] rounded-lg shadow-md overflow-hidden'
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={handleMouseLeave}
                  >
                    <video
                      ref={videoRef}
                      src={video.uploadUrl}
                      className='w-full h-full object-cover'
                      muted={false}
                      loop
                      playsInline
                    />
                    <AnimatePresence>
                      {showOverlay && (
                        <motion.div
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                          className='absolute inset-0 bg-gradient-to-t from-black/80 via-black/50 to-transparent flex flex-col justify-between p-4'
                        >
                          <div className='flex justify-between items-start'>
                            <div className='space-y-1'>
                              <VideoShowcaseStatusIndicator
                                isPublished={video.isPublishedOnInstagram}
                                platform='Instagram'
                              />
                              <VideoShowcaseButton
                                size='xs'
                                variant='secondary'
                                onClick={() => onPublish(video, 'Instagram')}
                              >
                                <Upload
                                  size={12}
                                  className='mr-1 inline-block'
                                />{' '}
                                Instagram
                              </VideoShowcaseButton>

                              <br />
                              {video.userEmail}
                              {video.createdAt}
                            </div>
                          </div>
                          <div className='space-y-4'>
                            <div>
                              <h3 className='text-white text-sm font-semibold truncate'>
                                {video.title}
                              </h3>
                              <p className='text-white/80 text-xs line-clamp-2'>
                                {video.description}
                              </p>
                            </div>
                            <div className='grid grid-cols-3 gap-2'></div>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ))}
              </div>
              <AnimatePresence>
                {selectedVideo && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className='fixed inset-0 bg-black/90 flex items-center justify-center z-50'
                  >
                    <div className='relative w-full max-w-4xl aspect-video'>
                      <video
                        src={selectedVideo.url}
                        className='w-full h-full object-contain rounded-lg shadow-2xl'
                        loop
                        muted={isFullscreenMuted}
                        playsInline
                        autoPlay={isFullscreenPlaying}
                      />
                      <div className='absolute top-4 right-4'>
                        <VideoShowcaseButton
                          variant='secondary'
                          size='sm'
                          onClick={handleClose}
                        >
                          <X className='h-4 w-4' />
                        </VideoShowcaseButton>
                      </div>
                      <div className='absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-4'>
                        <h3 className='text-xl font-semibold text-white'>
                          {selectedVideo.title}
                        </h3>
                        <p className='text-sm text-white/80 mt-1'>
                          {selectedVideo.description}
                        </p>
                        <div className='flex justify-between items-center mt-2'>
                          <div className='flex space-x-4'>
                            <VideoShowcaseStatusIndicator
                              isPublished={selectedVideo.isPublishedOnYouTube}
                              platform='YouTube'
                            />
                            <VideoShowcaseStatusIndicator
                              isPublished={selectedVideo.isPublishedOnTikTok}
                              platform='TikTok'
                            />
                          </div>
                          <div className='flex space-x-2'>
                            <VideoShowcaseButton
                              variant='secondary'
                              size='sm'
                              onClick={toggleFullscreenPlay}
                            >
                              {isFullscreenPlaying ? (
                                <Pause size={20} />
                              ) : (
                                <Play size={20} />
                              )}
                            </VideoShowcaseButton>
                            <VideoShowcaseButton
                              variant='secondary'
                              size='sm'
                              onClick={toggleFullscreenMute}
                            >
                              {isFullscreenMuted ? (
                                <VolumeX size={20} />
                              ) : (
                                <Volume2 size={20} />
                              )}
                            </VideoShowcaseButton>
                          </div>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>{' '}
    </div>
  );
};

export default ForIgVideos;
